interface IFormatDecimalOptions {
  maxFractionDigits?: number;
  minFractionDigits?: number;
  useThousandsSeparator?: boolean;
  fallback?: string;
}

const removeLeadingZeros = (value: string) =>
  value.replace(/^0+(?=\d)/, "");

export const normalizeDecimalString = (
  value: string | number | null | undefined
): string => {
  if (value === null || value === undefined) {
    return "0";
  }

  const raw = String(value).trim().replace(/,/g, "");
  if (!raw) {
    return "0";
  }

  if (!/^[-+]?\d*(\.\d*)?$/.test(raw)) {
    return "0";
  }

  const isNegative = raw.startsWith("-");
  const unsigned = raw.replace(/^[-+]/, "");
  const [integerPartRaw = "0", fractionRaw = ""] = unsigned.split(".");

  const integerPart = removeLeadingZeros(integerPartRaw || "0");
  const fractionPart = fractionRaw.replace(/[^0-9]/g, "");

  if (!fractionPart.length) {
    return `${isNegative ? "-" : ""}${integerPart}`;
  }

  return `${isNegative ? "-" : ""}${integerPart}.${fractionPart}`;
};

export const toScaledBigInt = (
  value: string | number | null | undefined,
  scale: number = 18
): bigint => {
  const normalized = normalizeDecimalString(value);
  const isNegative = normalized.startsWith("-");
  const unsigned = isNegative ? normalized.slice(1) : normalized;
  const [integerPart = "0", fractionPart = ""] = unsigned.split(".");

  const scaledFraction = `${fractionPart}${"0".repeat(scale)}`.slice(0, scale);
  const scaledValue = BigInt(`${integerPart}${scaledFraction}`);

  return isNegative ? -scaledValue : scaledValue;
};

export const compareDecimalStrings = (
  left: string | number | null | undefined,
  right: string | number | null | undefined,
  scale: number = 18
): number => {
  const leftValue = toScaledBigInt(left, scale);
  const rightValue = toScaledBigInt(right, scale);

  if (leftValue === rightValue) {
    return 0;
  }

  return leftValue > rightValue ? 1 : -1;
};

export const formatDecimalString = (
  value: string | number | null | undefined,
  {
    maxFractionDigits = 2,
    minFractionDigits = 0,
    useThousandsSeparator = true,
    fallback = "-",
  }: IFormatDecimalOptions = {}
): string => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return fallback;
  }

  const normalized = normalizeDecimalString(value);
  const isNegative = normalized.startsWith("-");
  const unsigned = isNegative ? normalized.slice(1) : normalized;
  const [integerPartRaw = "0", fractionPartRaw = ""] = unsigned.split(".");

  const integerPart = useThousandsSeparator ?
    integerPartRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ",") :
    integerPartRaw;

  if (maxFractionDigits <= 0) {
    return `${isNegative ? "-" : ""}${integerPart}`;
  }

  let fraction = fractionPartRaw.slice(0, maxFractionDigits);
  if (fraction.length < minFractionDigits) {
    fraction = fraction.padEnd(minFractionDigits, "0");
  } else if (minFractionDigits === 0) {
    fraction = fraction.replace(/0+$/, "");
  }

  return `${isNegative ? "-" : ""}${integerPart}${fraction ? `.${fraction}` : ""}`;
};
